import { useDisclosure, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CategoryForm from "./CategoryForm";
import * as React from "react";
import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import {
  organizationQuestionnaireKeys,
  treatmentOfSoldProductsKeys,
  useAddTreatmentOfSoldProductsItems,
  useDeleteTreatmentOfSoldProductsItem,
  useDownloadTreatmentOfSoldProductsTemplate,
  useGetTreatmentOfSoldProductsItems,
  useImportTreatmentOfSoldProductsItems,
  useUpdateTreatmentOfSoldProductsItem
} from "../../../../../../hooks/query";
import { useUpdateTreatmentOfSoldProductsCategory } from "../../../../../../hooks/query/scope-3";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { WasteGeneratedInOperationsModalWrapper } from "../CategoryModalWrappers";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";

function TreatmentOfSoldProducts({
                                   defaultValues,
                                   handleCategoryChange,
                                   tabKey,
                                   finalize,
                                   finalizeQuestionnaire,
                                   isPreview,
                                   organizationStructure,
                                   dimensionItems
                                 }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { id: questionnaireId } = defaultValues;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`inputs.wasteGeneratedInOperations.wasteActivity.name`),
    t(`inputs.wasteGeneratedInOperations.wasteType.name`),
    t(`inputs.wasteGeneratedInOperations.disposalMethod.name`),
    t(`inputs.wasteGeneratedInOperations.weight`)
  ];
  const attributesOrder = ["wasteActivity", "wasteType", "disposalMethod", "weight"];
  const FORM_INPUT_KEY = "treatmentOfSoldProducts";

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Treatment of Sold Products saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const {
    mutate: update,
    isLoading: isLoadingOnUpdate
  } = useUpdateTreatmentOfSoldProductsCategory(queryConfig);

  const formId = `formStep${tabKey}`;

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {};
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    const wasteActivityKey = "inputs.wasteGeneratedInOperations.wasteActivity.options";
    const wasteTypeKey = "inputs.wasteGeneratedInOperations.wasteType.options";
    const disposalMethodKey = "inputs.wasteGeneratedInOperations.disposalMethod.options";

    return items.map(item => {
      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        wasteActivityValue: item.wasteActivity,
        wasteActivity: t(`${wasteActivityKey}.${item.wasteActivity}`, { defaultValue: item.wasteActivity ?? "" }),
        wasteTypeValue: item.wasteType,
        wasteType: t(`${wasteTypeKey}.${item.wasteActivity}.${item.wasteType}`, { defaultValue: item.wasteType ?? "" }),
        disposalMethodValue: item.disposalMethod,
        disposalMethod: t(`${disposalMethodKey}.${item.disposalMethod}`, { defaultValue: item.disposalMethod ?? "" }),
        weight: item.weight ?? ""
      };
    });
  }, [t]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.treatmentOfSoldProducts.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        wasteActivity: item.wasteActivityValue,
        wasteType: item.wasteTypeValue,
        disposalMethod: item.disposalMethodValue,
        weight: item.weight
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.treatmentOfSoldProducts.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddTreatmentOfSoldProductsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(treatmentOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateTreatmentOfSoldProductsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(treatmentOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteTreatmentOfSoldProductsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(treatmentOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadTreatmentOfSoldProductsTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportTreatmentOfSoldProductsItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(treatmentOfSoldProductsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      wasteActivity: "",
      wasteType: "",
      disposalMethod: "",
      weight: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetTreatmentOfSoldProductsItems}
        isPreview={isPreview}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
        totalsConfig={{ valueKey: "weight" }}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <WasteGeneratedInOperationsModalWrapper
            tabKey={"wasteGeneratedInOperations"}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default TreatmentOfSoldProducts;
